/**
 * Trust AI Review Component
 * Simple auto-approval workflow for high-quality domain packs
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Loader2,
  Shield,
  FileCheck,
} from 'lucide-react';
import { startReview, approveTrustAI } from '../../lib/api/domainPacksApi';

interface TrustAIReviewProps {
  domainPackId: string;
  domainName: string;
  reviewerName: string;
  reviewerEmail: string;
  qualitySummary: any; // Quality assessment summary
  onComplete: () => void;
  onCancel: () => void;
  onSwitchMode: (mode: 'quick' | 'full') => void;
}

const TrustAIReview: React.FC<TrustAIReviewProps> = ({
  domainPackId,
  domainName,
  reviewerName,
  reviewerEmail,
  qualitySummary,
  onComplete,
  onCancel,
  onSwitchMode,
}) => {
  const queryClient = useQueryClient();
  const [reviewId, setReviewId] = useState<string | null>(null);

  const DEFAULT_THRESHOLD = 75.0;
  const overallScore = qualitySummary?.overall_score || 0;
  const isEligible = overallScore >= DEFAULT_THRESHOLD;

  // Start review mutation
  const startReviewMutation = useMutation({
    mutationFn: () =>
      startReview(domainPackId, {
        review_mode: 'trust_ai',
        reviewer_name: reviewerName,
        reviewer_email: reviewerEmail,
        min_score_threshold: DEFAULT_THRESHOLD,
      }),
    onSuccess: (data) => {
      setReviewId(data.review_id);
    },
    onError: (error: any) => {
      console.error('Failed to start Trust AI review:', error);
      alert(`Failed to start review: ${error.message || 'Unknown error'}`);
      onCancel();
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: () =>
      approveTrustAI(reviewId!, {
        review_id: reviewId!,
        confirm: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-pack-quality', domainPackId] });
      onComplete();
    },
    onError: (error: any) => {
      alert(`Failed to approve: ${error.message || 'Unknown error'}`);
    },
  });

  // Start review on mount
  useEffect(() => {
    startReviewMutation.mutate();
  }, []);

  const handleApprove = () => {
    if (!isEligible) {
      alert('Domain pack does not meet the minimum score threshold for Trust AI approval.');
      return;
    }

    if (
      confirm(
        `Approve "${domainName}" for publication using Trust AI?\n\nThis will create an audit trail of your decision.`
      )
    ) {
      approveMutation.mutate();
    }
  };

  // Loading state
  if (startReviewMutation.isPending || !reviewId) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Initializing Trust AI review...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <Shield className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Trust AI Auto-Approval</h2>
        <p className="text-neutral-600">{domainName}</p>
      </div>

      {/* Eligibility Status */}
      <div
        className={`mb-8 p-6 rounded-lg border-2 ${
          isEligible
            ? 'bg-green-50 border-green-300'
            : 'bg-red-50 border-red-300'
        }`}
      >
        <div className="flex items-start gap-4">
          {isEligible ? (
            <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
          ) : (
            <XCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
          )}
          <div className="flex-1">
            <h3
              className={`text-lg font-semibold mb-2 ${
                isEligible ? 'text-green-900' : 'text-red-900'
              }`}
            >
              {isEligible ? '✓ Quality Assessment Passed' : '✗ Does Not Meet Threshold'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-neutral-600 mb-1">Overall AI Score</div>
                <div
                  className={`text-3xl font-bold ${
                    isEligible ? 'text-green-900' : 'text-red-900'
                  }`}
                >
                  {overallScore.toFixed(1)}/100
                </div>
              </div>
              <div>
                <div className="text-sm text-neutral-600 mb-1">Required Threshold</div>
                <div className="text-3xl font-bold text-neutral-700">
                  {DEFAULT_THRESHOLD}/100
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isEligible && (
          <div className="mt-4 pt-4 border-t border-red-200">
            <p className="text-sm text-red-800">
              <strong>Recommendation:</strong> Use Quick Review or Full Review to manually assess
              the quality issues before approving.
            </p>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">📊 Summary</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
            <div className="text-sm text-neutral-600 mb-1">Subdomains Assessed</div>
            <div className="text-2xl font-bold text-neutral-900">
              {qualitySummary?.assessed_subdomains || 0}/{qualitySummary?.total_subdomains || 0}
            </div>
          </div>

          <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
            <div className="text-sm text-neutral-600 mb-1">Quality Rating</div>
            <div className="text-2xl font-bold text-neutral-900 capitalize">
              {qualitySummary?.overall_rating || 'Unknown'}
            </div>
          </div>
        </div>

        {/* Subdomain Breakdown */}
        {qualitySummary?.subdomain_breakdown && (
          <div className="mt-4 grid grid-cols-4 gap-2">
            {qualitySummary.subdomain_breakdown.excellent > 0 && (
              <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
                <div className="text-lg font-bold text-green-900">
                  {qualitySummary.subdomain_breakdown.excellent}
                </div>
                <div className="text-xs text-green-700">Excellent</div>
              </div>
            )}
            {qualitySummary.subdomain_breakdown.good > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                <div className="text-lg font-bold text-blue-900">
                  {qualitySummary.subdomain_breakdown.good}
                </div>
                <div className="text-xs text-blue-700">Good</div>
              </div>
            )}
            {qualitySummary.subdomain_breakdown.fair > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-center">
                <div className="text-lg font-bold text-yellow-900">
                  {qualitySummary.subdomain_breakdown.fair}
                </div>
                <div className="text-xs text-yellow-700">Fair</div>
              </div>
            )}
            {qualitySummary.subdomain_breakdown.poor > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
                <div className="text-lg font-bold text-red-900">
                  {qualitySummary.subdomain_breakdown.poor}
                </div>
                <div className="text-xs text-red-700">Poor</div>
              </div>
            )}
          </div>
        )}

        {/* Issues Summary */}
        {qualitySummary?.issues_by_severity && (
          <div className="mt-4">
            <div className="text-sm font-medium text-neutral-700 mb-2">Total Issues</div>
            <div className="flex gap-3">
              {qualitySummary.issues_by_severity.critical > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-red-900">
                    {qualitySummary.issues_by_severity.critical}
                  </span>
                  <span className="text-red-700">critical</span>
                </div>
              )}
              {qualitySummary.issues_by_severity.major > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  <span className="font-semibold text-orange-900">
                    {qualitySummary.issues_by_severity.major}
                  </span>
                  <span className="text-orange-700">major</span>
                </div>
              )}
              {qualitySummary.issues_by_severity.minor > 0 && (
                <div className="flex items-center gap-1 text-sm">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="font-semibold text-yellow-900">
                    {qualitySummary.issues_by_severity.minor}
                  </span>
                  <span className="text-yellow-700">minor</span>
                </div>
              )}
              {qualitySummary.issues_by_severity.critical === 0 &&
                qualitySummary.issues_by_severity.major === 0 &&
                qualitySummary.issues_by_severity.minor === 0 && (
                  <div className="flex items-center gap-1 text-sm text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>No issues requiring immediate attention</span>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {/* What You're Agreeing To */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          By clicking Approve, you are:
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>Trusting the AI quality assessment results</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>Approving publication without detailed manual review</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>Creating an audit trail of this approval decision</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4">
        {/* Primary Action */}
        <button
          onClick={handleApprove}
          disabled={!isEligible || approveMutation.isPending}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold text-lg transition-colors ${
            isEligible && !approveMutation.isPending
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
          }`}
        >
          {approveMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Approving...
            </>
          ) : (
            <>
              <FileCheck className="w-5 h-5" />
              Approve for Publication
            </>
          )}
        </button>

        {/* Alternative Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-neutral-600 hover:text-neutral-800 border border-neutral-300 rounded-lg"
          >
            Cancel
          </button>

          <div className="text-xs text-neutral-500">Or:</div>

          <button
            onClick={() => onSwitchMode('quick')}
            className="flex-1 px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 text-sm font-medium"
          >
            Switch to Quick Review
          </button>

          <button
            onClick={() => onSwitchMode('full')}
            className="flex-1 px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 text-sm font-medium"
          >
            Switch to Full Review
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrustAIReview;
